import MainWrapper from 'layouts/MainWrapper'
import { useThemeMediaQuery } from 'hooks/useThemeMediaQuery'
import Image from 'components/CompatImage'
import React from 'react'
import { Flex, MprolabIconContrast, Text, TextVariant, getSocialIconByName } from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import { Grid } from '@mui/material'
import { Link } from 'react-router-dom'
import { footerConfig, footerSocialsConfig } from 'config'
import { useRouter } from 'hooks/useRouter'
import DexTools from 'components/DexTools/DexTools'
import Dots from '../../public/images/dotsFooter.webp'

const StyledText = styled(Text)`
  white-space: pre-wrap;
`

const StyledMenuText = styled(Text)<{ disabled: boolean }>`
  transition: color 0.3s ease-in-out;
  :hover {
    color: ${({ theme, disabled }) => !disabled && theme.colors.purple};
  }
`

const ComingSoonLabel = styled(Flex)`
  padding: 4px 8px;
  height: max-content;
  width: max-content;
  background: ${({ theme }) => theme.colors.gray7};
  align-items: center;
  justify-content: center;
  border-radius: 32px;
`

const SmallText = styled(Text)`
  font-size: 10px;
  line-height: normal;
`

const StyledProductText = styled(Text)`
  font-size: 11px;
  color: rgba(209, 211, 218, 0.4);
  line-height: normal;
`

/**
 * A footer link to a document, routed rather than followed.
 *
 * `Link`, not an anchor: these are pages of this application, and an anchor
 * would reload the whole thing to move between two of them.
 */
const LegalLink = styled(Link)`
  color: ${({ theme }) => theme.colors.gray6};
  font-size: 12px;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.white};
    text-decoration: underline;
  }
`

const Footer: React.FC = () => {
  const { push } = useRouter()
  const { colors } = useTheme()
  const isTablet = useThemeMediaQuery('tablet')
  const hasFooterLinks = footerConfig.length > 0

  return (
    <MainWrapper>
      <Flex pt={isTablet ? '0px' : '64px'} pb="48px" flexDirection="column">
        <Flex mb={isTablet ? '32px' : '54px'}>
          <Image src={Dots} objectFit="contain" />
        </Flex>
        <Flex fullWidth justifyContent="space-between" flexDirection={isTablet ? 'column' : 'initial'}>
          <Flex flexDirection="column" gap={isTablet ? '24px' : '48px'} fullWidth>
            <MprolabIconContrast />
            <Flex maxWidth="357px">
              <StyledText variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                MPRO Lab is a DAO web platform for managing Licenses and MPRO distribution.
              </StyledText>
            </Flex>
          </Flex>
          <Flex
            fullWidth={hasFooterLinks}
            mt={isTablet ? '40px' : '0px'}
            justifyContent={isTablet ? 'flex-start' : 'flex-end'}
          >
            <Grid container>
              {footerConfig.map(({ header, links }, index) => {
                return (
                  <Grid item xs={6} sm={6} md={3.5} key={index}>
                    <Flex flexDirection="column" gap="8px">
                      <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{header}</Text>
                      {links.map(({ name, href, internalLink, disabled }, index) => {
                        return (
                          <Flex gap="8px" alignItems="center" key={index}>
                            <StyledMenuText
                              {...{ disabled }}
                              clickable={!disabled}
                              variant={TextVariant.BODY_SMALL}
                              key={index}
                              onClick={() => {
                                if (disabled) return
                                if (internalLink) {
                                  push(href, undefined, { shallow: true })
                                } else {
                                  window.open(href, '_blank')
                                }
                              }}
                            >
                              {name}
                            </StyledMenuText>
                            {disabled && (
                              <ComingSoonLabel>
                                <SmallText>Coming soon</SmallText>
                              </ComingSoonLabel>
                            )}
                          </Flex>
                        )
                      })}
                    </Flex>
                  </Grid>
                )
              })}
              <Grid item xs={12} sm={12} md={hasFooterLinks ? 5 : 'auto'} mt={isTablet ? '24px' : '0px'}>
                <Flex gap="8px" flexDirection="column">
                  <Text variant={TextVariant.BODY_DEFAULT_BOLD}>Community</Text>
                  <Flex flexDirection={isTablet ? 'initial' : 'column'} gap={isTablet ? '32px' : '8px'}>
                    {footerSocialsConfig.map((social, index) => {
                      const { header, socials, dexTools } = social
                      return (
                        <Flex gap="8px" flexDirection="column" key={index}>
                          <StyledProductText>{header}</StyledProductText>
                          <Flex gap="16px" flexWrap="wrap">
                            {socials.map((social, index) => {
                              const { name, href, disabled } = social
                              return (
                                <Flex key={index}>{getSocialIconByName({ platformName: name, href, disabled })}</Flex>
                              )
                            })}
                          </Flex>
                          {dexTools && <DexTools />}
                        </Flex>
                      )
                    })}
                  </Flex>
                </Flex>
              </Grid>
            </Grid>
          </Flex>
        </Flex>
        <Flex
          fullWidth
          justifyContent={isTablet ? 'center' : 'space-between'}
          alignItems="center"
          flexDirection={isTablet ? 'column' : 'row'}
          style={{ gap: '12px' }}
          mt="32px"
        >
          {/*
            The two documents anybody is entitled to read before connecting a
            wallet. They sit beside the copyright rather than in a column of
            their own: this is where people look for them, and a heading over
            two links is a heading about nothing.
          */}
          <Flex style={{ gap: '20px' }}>
            <LegalLink to="/documentation">Documentation</LegalLink>
            <LegalLink to="/terms-and-conditions">Terms &amp; Conditions</LegalLink>
          </Flex>
          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
            © 2026 MPRO Lab | All rights reserved
          </Text>
        </Flex>
      </Flex>
    </MainWrapper>
  )
}

export default Footer
